import React, { useState, useRef, useEffect } from "react";
import styled, { css } from "styled-components";

// Types
type DropdownMenuProps = {
  options: string[];
  onSelect?: (selectedOption: string) => void;
  placeholder?: string;
};

// Styled Components
const DropdownContainer = styled.div`
  position: relative;
  width: 10rem;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const DropdownHeader = styled.div<{ isOpen: boolean }>`
height:3.5rem;
  padding: 12px 16px;
  background-color:  #006ab2;
  color: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-radius: 8px 8px 0 0;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
    `}
`;

const DropdownList = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  background-color: #ffffff;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 8rem;
  overflow-y: auto;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;

  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    `}
`;

const DropdownItem = styled.li`
  padding: 12px 16px;
  color: #2d3748;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #f7fafc;
  }
`;

const ArrowIcon = styled.span<{ isOpen: boolean }>`
  transition: transform 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

// Composant DropdownMenu
const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  onSelect,
  placeholder = "Sélectionnez une option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownHeader isOpen={isOpen} onClick={toggleDropdown}>
        {selectedOption || placeholder}
        <ArrowIcon isOpen={isOpen}>▼</ArrowIcon>
      </DropdownHeader>
      <DropdownList isOpen={isOpen}>
        {options.map((option, index) => (
          <DropdownItem key={index} onClick={() => handleOptionClick(option)}>
            {option}
          </DropdownItem>
        ))}
      </DropdownList>
    </DropdownContainer>
  );
};

export default DropdownMenu;
